import type {
  FirelightPrefillCatalogApplication,
  FirelightPrefillCatalogApplicationInput,
  FirelightPrefillCatalogMeta,
} from '@fig/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import apiClient from '../../lib/api-client';

export interface FirelightPrefillCatalogResponse {
  meta: FirelightPrefillCatalogMeta;
  applications: FirelightPrefillCatalogApplication[];
}

export interface FirelightPrefillApplicationResponse {
  application: FirelightPrefillCatalogApplication;
}

export const firelightPrefillKey = ['admin', 'firelight-prefill'] as const;
export const firelightPrefillApplicationKey = (id: string) =>
  ['admin', 'firelight-prefill', 'application', id] as const;

async function fetchCatalog(): Promise<FirelightPrefillCatalogResponse> {
  const response = await apiClient.get<FirelightPrefillCatalogResponse>('/admin/firelight-prefill');
  return response.data;
}

async function fetchApplication(id: string): Promise<FirelightPrefillCatalogApplication> {
  const response = await apiClient.get<FirelightPrefillApplicationResponse>(
    `/admin/firelight-prefill/applications/${id}`
  );
  return response.data.application;
}

export function useFirelightPrefillCatalog() {
  return useQuery({
    queryKey: firelightPrefillKey,
    queryFn: fetchCatalog,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
}

export function useFirelightPrefillApplication(id: string | null) {
  return useQuery({
    queryKey: id
      ? firelightPrefillApplicationKey(id)
      : ['admin', 'firelight-prefill', 'application', 'none'],
    queryFn: () => fetchApplication(id as string),
    enabled: Boolean(id),
    refetchOnWindowFocus: false,
  });
}

export function useCreateFirelightPrefillApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: FirelightPrefillCatalogApplicationInput) => {
      const response = await apiClient.post<FirelightPrefillApplicationResponse>(
        '/admin/firelight-prefill/applications',
        input
      );
      return response.data.application;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firelightPrefillKey });
    },
  });
}

export function useUpdateFirelightPrefillApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
      input: FirelightPrefillCatalogApplicationInput;
    }) => {
      const response = await apiClient.put<FirelightPrefillApplicationResponse>(
        `/admin/firelight-prefill/applications/${id}`,
        input
      );
      return response.data.application;
    },
    onSuccess: (application) => {
      queryClient.invalidateQueries({ queryKey: firelightPrefillKey });
      queryClient.setQueryData(firelightPrefillApplicationKey(application.id), application);
    },
  });
}

export function useDeleteFirelightPrefillApplication() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/admin/firelight-prefill/applications/${id}`);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: firelightPrefillKey });
    },
  });
}
